import { appRoutePath } from '../../router/util/app-route-path'
import { useLocalizationStore } from '../../localization/store/localization.store'
import { observer } from 'mobx-react-lite'
import { NavModel } from '../../../model/Nav'
import { Sidebar } from '../../sidebar/component/Sidebar'
import { mergeTranslationWithObjects } from '../../localization/util/localization.helper'
import { useUserStore } from 'src/feature/user/store/user.store'
import { useEffect, useState } from 'react'
import { AppCircleLoader } from 'src/component/common/analog/molecule/loader/AppCircleLoader'
import { useAuthStore } from 'src/feature/auth/store/auth.store'
import { OrganisationRole } from '../model/OrganisationRole'
import { Collapse, Icon } from '@mui/material'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { TranslatableText } from 'src/feature/localization/component/atom/TranslatableText'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import { If } from 'src/component/common/original/atom/If'
import { AppUnstyledButton } from 'src/component/common/analog/molecule/button/AppUnstyledButton'
import { iconMapping } from 'src/util/icon-mapping'
import { useTourSteps } from 'src/hook/useTourSteps'
import { expandedSidebarDropdownSx } from 'src/feature/sidebar/component/sidebarDropdownStyles'

export type ControlPanelSidebarProps = {
  isOpen: boolean
}

export const ControlPanelSidebar = observer(({ isOpen }: ControlPanelSidebarProps) => {
  const { translation } = useLocalizationStore()
  const userStore = useUserStore()
  const authStore = useAuthStore()
  const { isLoading } = userStore.getGenericFieldsState('handleGetOrganisation')

  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { controlPanelSidebar } = translation.components
  const { getStepClass } = useTourSteps()
  
  const navLinks: NavModel<'props.link'> = {
    controlpanelUsers: {
      props: {
        link: appRoutePath.controlpanelUsers,
        hidden: !authStore.hasRole(OrganisationRole.UserAdministrator),
      },
    },
    controlpanelOrganisation: {
      props: {
        link: appRoutePath.controlpanelOrganisation,
        hidden: !authStore.hasRole(OrganisationRole.UserAdministrator),
      },
    },
    controlpanelModules: {
      props: {
        link: appRoutePath.controlpanelModules,
        hidden: !authStore.hasRole(OrganisationRole.SystemAdministrator),
      },
    },
    controlpanelSystem: {
      props: {
        link: appRoutePath.controlpanelSystem,
        hidden: !authStore.hasRole(OrganisationRole.SystemAdministrator),
      },
    },
  }

  const desktopNav = mergeTranslationWithObjects(controlPanelSidebar.nav, navLinks) as unknown as NavModel

  useEffect(() => {
    userStore.handleGetOrganisation()
  }, [])

  if (isLoading) {
    return <AppCircleLoader isShowing={true} />
  }

  return (
    <>
      <AppBox
        alignItems='center'
        width='100%'
        sx={{
          cursor: 'pointer',
          justifyContent: {
            xs: 'center',
            md: 'space-between',
          } }}
        onClick={() => isOpen && setIsMenuOpen(!isMenuOpen)}
      >
        <AppBox justifyContent='center' alignItems='center' gap={7} className={getStepClass('controlpanel')}>
          <Icon
            sx={{
              color: 'text.primary',
              transition: 'color 0.3s ease',
              fontSize: 16,
              height: 16,
              width: 16,
            }}
            component={iconMapping['SettingsRoundedIcon']}
          />
          {isOpen && <TranslatableText
            component={'p'}
            textValue={controlPanelSidebar.title()}
            sx={{
              transition: 'color 0.3s ease',
              color: 'text.primary',
              fontSize: '14px',
            }}
          />}
        </AppBox>
        <AppUnstyledButton
          size='small'
          sx={{
            width: 20,
            height: 20,
            minWidth: 20,
            minHeight: 20,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            backgroundColor: isMenuOpen && isOpen ? 'rgba(125, 235, 228, 0.2)' : 'transparent',
            transition: 'background-color 0.2s ease',
            '&:hover': {
              backgroundColor: 'rgba(125, 235, 228, 0.12)',
            },
          }}
        >
          <If condition={isMenuOpen && isOpen}>
            <ExpandMoreRoundedIcon sx={{ color: 'text.primary', fontSize: 16, height: 16, width: 16 }} />
          </If>
          <If condition={!isMenuOpen && isOpen}>
            <ChevronRightRoundedIcon sx={{ color: 'text.primary', fontSize: 16, height: 16, width: 16 }} />
          </If>
        </AppUnstyledButton>
      </AppBox>
      <Collapse
        in={isMenuOpen && isOpen}
        sx={{
          width: '100%',
          ...expandedSidebarDropdownSx,
          '& .link-label': {
            fontSize: '14px',
          },
        }}
        mountOnEnter
        unmountOnExit
      >
        <Sidebar nav={desktopNav} />
      </Collapse>
    </>
  )
})